import React, { useEffect, useState } from "react";
import { useUserStore } from "../store/UserStore";
import { useAuth } from "../store/Auth";
import ClipLoader from "react-spinners/ClipLoader";
import { Link } from "react-router-dom";
import { base, agent_stats } from "../constants/urls";
import Header from "../components/assist/Title";
import { checkToken } from "../components/CheckToken";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from "recharts";

const formatMoney = (amount) => {
  return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(amount || 0);
};
const formatNumber = (num) => {
  return new Intl.NumberFormat('en-US').format(num || 0);
};

export default function Dashboard() {
  const userDetails = useUserStore((state) => state.userDetails);
  const getAuth = useAuth((state) => state.auth);
  
  // Analytics State
  const [statsData, setStatsData] = useState([]);
  const [isStatsLoading, setIsStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);
  
  // Filters State
  const [datePreset, setDatePreset] = useState("this_month"); 
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [triggerFetch, setTriggerFetch] = useState(0);

  // Modal State
  const [showNoticeModal, setShowNoticeModal] = useState(false);

  // --- Auth Check ---
  useEffect(() => {
    const verifySession = async () => {
      if (getAuth?.authenticated && getAuth?.token) {
        const isValid = await checkToken(getAuth.token);
        if (!isValid) {
          if (window.CMRWidget && typeof window.CMRWidget.clearSession === "function") {
            window.CMRWidget.clearSession();
          }
          localStorage.clear();
          window.location.replace("/login");
        }
      } else if (getAuth?.authenticated === false) {
        window.location.replace("/login");
      }
    };
    verifySession();
  }, [getAuth?.authenticated, getAuth?.token]);

  // --- Notice Modal Check ---
  useEffect(() => {
    // Only show modal to Tiers 2 & 3
    if ((userDetails.accountTier === "2" || userDetails.accountTier === "3") && getAuth?.token) {
      // Create a unique key based on the current login token
      // This ensures a new login = new session = modal shows again
      const tokenSnippet = getAuth.token.substring(0, 15);
      const modalKey = `hasSeenNoticeModal_${tokenSnippet}`;
      
      const hasSeenModal = sessionStorage.getItem(modalKey);
      
      if (!hasSeenModal) {
        setShowNoticeModal(true);
        sessionStorage.setItem(modalKey, "true");
      }
    }
  }, [userDetails.accountTier, getAuth?.token]);

  // --- Fetch API ---
  useEffect(() => {
    const fetchStats = async () => {
      // Only fetch stats data for Tier 1 users
      if (userDetails.accountTier !== "1") return; 

      setIsStatsLoading(true);
      setStatsError(null);
      
      try {
        let url = `${base}${agent_stats}?`;
        const params = new URLSearchParams();
        
        if (datePreset !== "custom" && datePreset !== "") {
          params.append("date_preset", datePreset);
        } else if (datePreset === "custom") {
          if (customStartDate) params.append("start_date", customStartDate);
          if (customEndDate) params.append("end_date", customEndDate);
        }

        const response = await fetch(url + params.toString(), {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${getAuth.token}`
          }
        });

        const json = await response.json();
        if (json.statusCode === 0) {
          setStatsData(json.data || []);
        } else {
          setStatsError(json.message || "Failed to fetch statistics");
        }
      } catch (err) {
        setStatsError(err.message || "Network error occurred");
      } finally {
        setIsStatsLoading(false);
      }
    };

    if (getAuth?.token) {
      fetchStats();
    }
  }, [getAuth?.token, userDetails.accountTier, datePreset, triggerFetch]);

  // Calculate top-level metrics
  const grandTotalAmountDue = statsData.reduce((acc, curr) => acc + curr.amount_due, 0);
  const totalPaidEntries = statsData.reduce((acc, curr) => acc + curr.total_entries_paid, 0);
  const totalUnpaidEntries = statsData.reduce((acc, curr) => acc + curr.total_entries_unpaid, 0);

  // Top 10 by Amount Due
  const chartData = statsData.slice(0, 10).map(agent => ({
    name: agent.agent_name,
    "Amount Due": agent.amount_due
  }));

  const categoryData = [
    { name: 'Vehicle (4W+)', value: statsData.reduce((acc, curr) => acc + (curr.category_breakdown?.motorvehicle?.count || 0), 0) },
    { name: 'Motorcycle (2-3W)', value: statsData.reduce((acc, curr) => acc + (curr.category_breakdown?.motorcycle?.count || 0), 0) }
  ].filter(item => item.value > 0);

  const handleApplyCustomDate = () => {
    if(customStartDate && customEndDate) setTriggerFetch(prev => prev + 1);
  };

  return (
    <div className="bg-gray-50 min-h-screen pb-10 font-sans relative">
      <Header />

      {/* --- IMPORTANT NOTICE MODAL --- */}
      {showNoticeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-60 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 md:p-8 relative">
            
            {/* Close Icon */}
            <button 
              onClick={() => setShowNoticeModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="flex flex-col items-center text-center mb-6">
              <div className="bg-yellow-100 p-4 rounded-full mb-4">
                <svg className="w-10 h-10 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-2xl font-black text-gray-800">Compliance Notice</h3>
              <p className="text-gray-500 mt-2 text-sm">Please read the following guidelines carefully before proceeding.</p>
            </div>
            
            <ul className="space-y-4 mb-8 text-sm md:text-base text-gray-700 bg-gray-50 p-5 rounded-xl border border-gray-100">
              <li className="flex items-start gap-3">
                <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <span>Do <span className="font-bold">not</span> register a vehicle with a CMR certificate to another person or organization.</span>
              </li>
              <li className="flex items-start gap-3">
                <svg className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <span>If purchased or transferred, the seller/former owner must initiate a <span className="font-bold">Change of Ownership</span>.</span>
              </li>
              <li className="flex items-start gap-3">
                <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <span>Do <span className="font-bold">not</span> re-register a vehicle already existing in an applicant’s profile.</span>
              </li>
            </ul>

            <button 
              onClick={() => setShowNoticeModal(false)}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3.5 px-6 rounded-xl transition shadow-md"
            >
              I Understand, Continue
            </button>
          </div>
        </div>
      )}
      {/* ---------------------------------- */}
      
      {/* Universal Support Notice (ALL TIERS) */}
      <div className="container mx-auto px-4 pt-6 pb-2">
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg shadow-sm max-w-7xl mx-auto flex items-start gap-3">
          <svg className="w-6 h-6 text-blue-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-blue-800 text-sm md:text-base font-medium">
            Need help? Please make use of the <span className="font-bold">CMR Support widget</span> located at the lower right corner of the page for any assistance.
          </p>
        </div>
      </div>

      {/* AGENT DASHBOARD (ONLY TIER 2 & 3) */}
      {(userDetails.accountTier === "2" || userDetails.accountTier === "3") && (
        <div className="container mx-auto px-4 max-w-5xl mt-6 mb-12 animate-fade-in">
          
          {/* Welcome Header */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8 mb-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-extrabold text-gray-800 mb-2">Welcome to your Workspace</h1>
              <p className="text-gray-500 text-sm md:text-base">Manage vehicle registrations and renewals securely and efficiently.</p>
            </div>
            <div className="bg-green-50 text-green-700 px-4 py-2 rounded-lg font-semibold text-sm border border-green-100">
              Agent Status: Active
            </div>
          </div>

          {/* Action Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            
            {/* New Registration Card */}
            <div className="bg-white rounded-2xl shadow-sm hover:shadow-lg border border-gray-100 p-6 md:p-8 transition duration-300 flex flex-col h-full group">
              <div className="w-14 h-14 bg-green-50 text-green-600 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">New Request</h3>
              <p className="text-gray-500 mb-6 flex-grow text-sm leading-relaxed">
                Start a fresh application to register a new vehicle or motorcycle and generate a standard CMR certificate.
              </p>
              <Link to="/services" className="w-full bg-green-600 hover:bg-green-700 text-white text-center font-bold py-3.5 px-6 rounded-xl transition flex justify-center items-center gap-2">
                Start New Request
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
              </Link>
            </div>

            {/* Renewal Card */}
            <div className="bg-white rounded-2xl shadow-sm hover:shadow-lg border border-gray-100 p-6 md:p-8 transition duration-300 flex flex-col h-full group">
              <div className="w-14 h-14 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Process Renewal</h3>
              <p className="text-gray-500 mb-6 flex-grow text-sm leading-relaxed">
                Search for an existing vehicle in the database to process the annual renewal of their CMR certificate.
              </p>
              <Link to="/vehiclesearch" className="w-full bg-blue-600 hover:bg-blue-700 text-white text-center font-bold py-3.5 px-6 rounded-xl transition flex justify-center items-center gap-2">
                Start Renewal
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
              </Link>
            </div>

          </div>
        </div>
      )}

      {/* ADMIN DASHBOARD (ONLY TIER 1) */}
      {userDetails.accountTier === "1" && (
        <div className="container mx-auto px-4 max-w-[1400px] mb-10">
          
          {/* Header & Advanced Filters */}
          <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center mb-6 mt-4 gap-4 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
            <h2 className="text-2xl font-bold text-gray-800">Workstation Manager Performance & Earnings</h2>
            
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-gray-500 font-medium text-sm">Filter:</span>
              <select 
                className="bg-gray-50 border border-gray-200 text-gray-700 font-semibold rounded-lg px-3 py-2 outline-none cursor-pointer focus:ring-2 focus:ring-green-500"
                value={datePreset}
                onChange={(e) => setDatePreset(e.target.value)}
              >
                <option value="">All Time</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="this_week">This Week</option>
                <option value="this_month">This Month</option>
                <option value="last_month">Last Month</option>
                <option value="this_year">This Year</option>
                <option value="custom">Custom Range</option>
              </select>

              {datePreset === "custom" && (
                <div className="flex items-center gap-2 fade-in">
                  <input 
                    type="date" 
                    value={customStartDate} 
                    onChange={e => setCustomStartDate(e.target.value)} 
                    className="border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-green-500"
                  />
                  <span className="text-gray-400">to</span>
                  <input 
                    type="date" 
                    value={customEndDate} 
                    onChange={e => setCustomEndDate(e.target.value)} 
                    className="border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-green-500"
                  />
                  <button 
                    onClick={handleApplyCustomDate}
                    disabled={!customStartDate || !customEndDate}
                    className="bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white font-bold py-2 px-4 rounded-lg text-sm transition"
                  >
                    Apply
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Loading / Error States */}
          {isStatsLoading ? (
            <div className="flex justify-center items-center h-64 bg-white rounded-xl shadow-sm">
              <ClipLoader color="#166434" size={50} />
            </div>
          ) : statsError ? (
            <div className="bg-red-50 text-red-600 p-6 rounded-xl text-center border border-red-200 shadow-sm">
              <p className="font-semibold text-lg">{statsError}</p>
            </div>
          ) : statsData.length === 0 ? (
            <div className="bg-white p-10 rounded-xl text-center text-gray-500 shadow-sm border border-gray-100 h-64 flex items-center justify-center">
              <p className="text-lg font-medium">No records found for the selected time period.</p>
            </div>
          ) : (
            <>
              {/* Summary Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-l-green-500">
                  <p className="text-xs font-bold text-gray-500 mb-1 uppercase tracking-wider">Total Amount Due</p>
                  <h3 className="text-3xl font-black text-green-600">{formatMoney(grandTotalAmountDue)}</h3>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-l-blue-500">
                  <p className="text-xs font-bold text-gray-500 mb-1 uppercase tracking-wider">Entries (Completed / Incomplete)</p>
                  <div className="flex items-baseline gap-2 mt-1">
                    <h3 className="text-3xl font-black text-gray-800">{formatNumber(totalPaidEntries)}</h3>
                    <span className="text-base font-medium text-orange-400">/ {formatNumber(totalUnpaidEntries)}</span>
                  </div>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-l-purple-500">
                  <p className="text-xs font-bold text-gray-500 mb-1 uppercase tracking-wider">Active Workstation Managers</p>
                  <h3 className="text-3xl font-black text-gray-800">{formatNumber(statsData.length)}</h3>
                </div>
              </div>

              {/* Charts Section */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                {/* Bar Chart: Top Managers */}
                <div className="bg-white rounded-xl shadow-sm p-6 lg:col-span-2 border border-gray-100">
                  <h3 className="text-lg font-bold text-gray-700 mb-4">Top 10 Workstation Managers by Amount Due </h3>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                        <XAxis dataKey="name" tick={{fontSize: 12}} tickFormatter={(val) => val.length > 10 ? `${val.substring(0, 10)}...` : val} />
                        <YAxis tickFormatter={(val) => `₦${val/1000}k`} tick={{fontSize: 12}} />
                        <RechartsTooltip formatter={(value) => formatMoney(value)} cursor={{fill: '#f3f4f6'}} />
                        <Bar dataKey="Amount Due" fill="#10b981" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Pie Chart: Categories */}
                <div className="bg-white rounded-xl shadow-sm p-6 lg:col-span-1 border border-gray-100">
                  <h3 className="text-lg font-bold text-gray-700 mb-4 text-center">Request by Type</h3>
                  <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={categoryData} cx="50%" cy="45%" innerRadius={60} outerRadius={90} paddingAngle={5} dataKey="value"
                        >
                          {categoryData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={index === 0 ? '#3b82f6' : '#f59e0b'} />
                          ))}
                        </Pie>
                        <RechartsTooltip />
                        <Legend verticalAlign="bottom" height={36}/>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* Data Pivot Table */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
                  <h3 className="text-lg font-bold text-gray-800">Workstation Manager Pivot Data</h3>
                  <span className="text-xs bg-green-100 text-green-800 py-1 px-3 rounded-full font-bold">
                    Sorted by Amount Due
                  </span>
                </div>
                
                <div className="overflow-x-auto max-h-[600px]">
                  <table className="w-full text-left text-sm text-gray-600 relative">
                    <thead className="text-xs text-gray-500 uppercase bg-gray-50 sticky top-0 shadow-sm z-10">
                      <tr>
                        <th className="px-5 py-4 font-bold border-b">Rank</th>
                        <th className="px-5 py-4 font-bold border-b">Manager Info</th>
                        <th className="px-5 py-4 font-bold text-center border-b">Entries (Paid / Unpaid)</th>
                        <th className="px-5 py-4 font-bold text-right border-b bg-green-50/50 text-green-700">Amount Due (Paid)</th>
                        <th className="px-5 py-4 font-bold text-center border-b border-l border-gray-200">New / Renew</th>
                        <th className="px-5 py-4 font-bold text-center border-b border-l border-gray-200">Vehicles / Bikes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statsData.map((agent, index) => {
                        const countNew = agent.entry_type_breakdown?.new?.count || 0;
                        const countRenewal = agent.entry_type_breakdown?.renewal?.count || 0;
                        const countVehicle = agent.category_breakdown?.motorvehicle?.count || 0;
                        const countBike = agent.category_breakdown?.motorcycle?.count || 0;

                        return (
                          <tr key={agent.agent_id} className="border-b hover:bg-gray-50 transition-colors">
                            <td className="px-5 py-4 font-semibold text-gray-900">{index + 1}</td>
                            <td className="px-5 py-4 whitespace-nowrap">
                              <p className="font-bold text-gray-800 uppercase">{agent.agent_name}</p>
                              <p className="text-xs text-gray-400">ID: {agent.agent_id}</p>
                            </td>
                            
                            <td className="px-5 py-4 text-center">
                              <span className="font-bold text-green-600">{formatNumber(agent.total_entries_paid)}</span>
                              <span className="text-gray-300 mx-1">/</span>
                              <span className="font-medium text-orange-400">{formatNumber(agent.total_entries_unpaid)}</span>
                            </td>

                            <td className="px-5 py-4 font-black text-right text-green-600 bg-green-50/20">
                              {formatMoney(agent.amount_due)}
                            </td>
                            
                            {/* Entry Type Breakdown */}
                            <td className="px-5 py-4 border-l border-gray-100">
                              <div className="flex justify-center items-center gap-2 text-xs">
                                <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded w-12 text-center" title="New Registrations">
                                  {formatNumber(countNew)} N
                                </span>
                                <span className="bg-orange-50 text-orange-700 px-2 py-1 rounded w-12 text-center" title="Renewals">
                                  {formatNumber(countRenewal)} R
                                </span>
                              </div>
                            </td>

                            {/* Category Breakdown */}
                            <td className="px-5 py-4 border-l border-gray-100">
                              <div className="flex justify-center items-center gap-2 text-xs">
                                <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded border border-gray-200" title="Motorvehicles (4W+)">
                                  🚗 {formatNumber(countVehicle)}
                                </span>
                                <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded border border-gray-200" title="Motorcycles (2-3W)">
                                  🏍️ {formatNumber(countBike)}
                                </span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}